import DungeonConfig, { TILE, ROOM_TYPE } from './DungeonConfig.js';

/**
 * Seeded PRNG (mulberry32).
 */
function createRNG(seed) {
  let s = seed | 0;
  return function () {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * DungeonLayout - Generates a 2D tile grid with rooms and corridors.
 */
export default class DungeonLayout {
  constructor(config = {}) {
    this.width = config.gridWidth || DungeonConfig.GRID_WIDTH;
    this.height = config.gridHeight || DungeonConfig.GRID_HEIGHT;
  }

  /**
   * Generate a complete level layout.
   * @param {Object} options
   * @param {number} [options.roomCount]
   * @param {number} [options.seed]
   * @returns {{ grid: number[][], rooms: Array, corridors: Array }}
   */
  generate(options = {}) {
    const roomCount = options.roomCount || DungeonConfig.MIN_ROOMS;
    const seed = options.seed || Date.now();
    this.rng = createRNG(seed);

    // Initialize grid
    this.grid = [];
    for (let x = 0; x < this.width; x++) {
      this.grid[x] = new Array(this.height).fill(TILE.VOID);
    }

    // Phase 1: Place rooms
    this.rooms = this._placeRooms(roomCount);

    // Phase 2: Assign room types
    this._assignRoomTypes();

    // Phase 3: Build MST and connect with corridors
    this.corridors = this._connectRooms();

    // Phase 4: Compute walls
    this._computeWalls();

    return {
      grid: this.grid,
      rooms: this.rooms,
      corridors: this.corridors
    };
  }

  // ---- Room Placement ----

  _placeRooms(targetCount) {
    const rooms = [];
    let attempts = 0;
    let nextId = 0;

    while (rooms.length < targetCount && attempts < DungeonConfig.MAX_PLACEMENT_ATTEMPTS) {
      attempts++;

      const w = this._randInt(DungeonConfig.MIN_ROOM_SIZE, DungeonConfig.MAX_ROOM_SIZE);
      const d = this._randInt(DungeonConfig.MIN_ROOM_SIZE, DungeonConfig.MAX_ROOM_SIZE);
      const x = this._randInt(2, this.width - w - 2);
      const z = this._randInt(2, this.height - d - 2);

      // Check overlap with margin
      const margin = DungeonConfig.ROOM_MARGIN;
      let overlaps = false;
      for (const room of rooms) {
        if (x - margin < room.x + room.width + margin &&
            x + w + margin > room.x - margin &&
            z - margin < room.z + room.depth + margin &&
            z + d + margin > room.z - margin) {
          overlaps = true;
          break;
        }
      }
      if (overlaps) continue;

      const room = {
        id: nextId++,
        x, z, width: w, depth: d,
        type: ROOM_TYPE.NORMAL,
        center: { x: x + w / 2, z: z + d / 2 }
      };

      // Mark floor tiles
      for (let rx = x; rx < x + w; rx++) {
        for (let rz = z; rz < z + d; rz++) {
          this.grid[rx][rz] = TILE.FLOOR;
        }
      }

      rooms.push(room);
    }

    return rooms;
  }

  // ---- Room Type Assignment ----

  _assignRoomTypes() {
    if (this.rooms.length === 0) return;

    // Start room: first placed
    this.rooms[0].type = ROOM_TYPE.START;

    // Exit room: farthest from start
    const start = this.rooms[0].center;
    let farthestDist = 0;
    let farthestIdx = this.rooms.length - 1;
    for (let i = 1; i < this.rooms.length; i++) {
      const c = this.rooms[i].center;
      const dist = (c.x - start.x) ** 2 + (c.z - start.z) ** 2;
      if (dist > farthestDist) {
        farthestDist = dist;
        farthestIdx = i;
      }
    }
    this.rooms[farthestIdx].type = ROOM_TYPE.EXIT;

    // Assign special rooms
    const normalRooms = this.rooms.filter(r =>
      r.type === ROOM_TYPE.NORMAL
    );

    if (normalRooms.length > 0) {
      // 1 treasure room
      const treasureIdx = Math.floor(this.rng() * normalRooms.length);
      normalRooms[treasureIdx].type = ROOM_TYPE.TREASURE;
      normalRooms.splice(treasureIdx, 1);
    }

    if (normalRooms.length > 2) {
      // 1 boss room (pick a large one)
      normalRooms.sort((a, b) => (b.width * b.depth) - (a.width * a.depth));
      normalRooms[0].type = ROOM_TYPE.BOSS;
      normalRooms.splice(0, 1);
    }

    if (normalRooms.length > 1) {
      // 1 shrine room
      const shrineIdx = Math.floor(this.rng() * normalRooms.length);
      normalRooms[shrineIdx].type = ROOM_TYPE.SHRINE;
    }
  }

  // ---- Corridor Connection (MST + extra edges) ----

  _connectRooms() {
    if (this.rooms.length < 2) return [];

    // Build complete graph of room distances
    const n = this.rooms.length;
    const edges = [];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const ci = this.rooms[i].center;
        const cj = this.rooms[j].center;
        const dist = Math.abs(ci.x - cj.x) + Math.abs(ci.z - cj.z);
        edges.push({ i, j, dist });
      }
    }
    edges.sort((a, b) => a.dist - b.dist);

    // Kruskal's MST with union-find
    const parent = Array.from({ length: n }, (_, i) => i);
    const find = (x) => {
      while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; }
      return x;
    };
    const union = (a, b) => { parent[find(a)] = find(b); };

    const mstEdges = [];
    const nonMstEdges = [];

    for (const edge of edges) {
      if (find(edge.i) !== find(edge.j)) {
        union(edge.i, edge.j);
        mstEdges.push(edge);
      } else {
        nonMstEdges.push(edge);
      }
    }

    // Add extra edges for loops
    const extraCount = Math.min(DungeonConfig.EXTRA_CONNECTIONS, nonMstEdges.length);
    for (let i = 0; i < extraCount; i++) {
      const idx = Math.floor(this.rng() * Math.min(nonMstEdges.length, 10));
      mstEdges.push(nonMstEdges.splice(idx, 1)[0]);
    }

    // Carve corridors
    const corridors = [];
    for (const edge of mstEdges) {
      const corridor = this._carveCorridorL(
        this.rooms[edge.i].center,
        this.rooms[edge.j].center
      );
      corridors.push(corridor);
    }

    return corridors;
  }

  /**
   * Carve an L-shaped corridor between two points.
   * Horizontal first, then vertical.
   */
  _carveCorridorL(a, b) {
    const cw = DungeonConfig.CORRIDOR_WIDTH;
    const segments = [];

    const ax = Math.floor(a.x);
    const az = Math.floor(a.z);
    const bx = Math.floor(b.x);
    const bz = Math.floor(b.z);

    // Horizontal segment
    const minX = Math.min(ax, bx);
    const maxX = Math.max(ax, bx);
    if (maxX > minX) {
      const seg = { x: minX, z: az, width: maxX - minX + cw, depth: cw };
      segments.push(seg);
      this._fillFloor(seg.x, seg.z, seg.width, seg.depth);
    }

    // Vertical segment
    const minZ = Math.min(az, bz);
    const maxZ = Math.max(az, bz);
    if (maxZ > minZ) {
      const seg = { x: bx, z: minZ, width: cw, depth: maxZ - minZ + cw };
      segments.push(seg);
      this._fillFloor(seg.x, seg.z, seg.width, seg.depth);
    }

    // Corner piece (where the L bends)
    if (maxX > minX && maxZ > minZ) {
      this._fillFloor(bx, az, cw, cw);
    }

    return { segments };
  }

  _fillFloor(x, z, w, d) {
    for (let rx = x; rx < x + w && rx < this.width; rx++) {
      for (let rz = z; rz < z + d && rz < this.height; rz++) {
        if (rx >= 0 && rz >= 0) {
          this.grid[rx][rz] = TILE.FLOOR;
        }
      }
    }
  }

  // ---- Wall Computation ----

  _computeWalls() {
    // Any VOID tile adjacent (8-connected) to a FLOOR tile becomes a WALL
    const wallPositions = [];

    for (let x = 0; x < this.width; x++) {
      for (let z = 0; z < this.height; z++) {
        if (this.grid[x][z] !== TILE.VOID) continue;

        let adjacentFloor = false;
        for (let dx = -1; dx <= 1; dx++) {
          for (let dz = -1; dz <= 1; dz++) {
            if (dx === 0 && dz === 0) continue;
            const nx = x + dx;
            const nz = z + dz;
            if (nx >= 0 && nx < this.width && nz >= 0 && nz < this.height) {
              if (this.grid[nx][nz] === TILE.FLOOR) {
                adjacentFloor = true;
                break;
              }
            }
          }
          if (adjacentFloor) break;
        }

        if (adjacentFloor) {
          wallPositions.push({ x, z });
        }
      }
    }

    for (const pos of wallPositions) {
      this.grid[pos.x][pos.z] = TILE.WALL;
    }
  }

  // ---- Utilities ----

  _randInt(min, max) {
    return min + Math.floor(this.rng() * (max - min + 1));
  }
}
